import { Component, Input, OnInit, Renderer2, OnChanges } from "@angular/core";
import { InvoiceReviewSummaryService } from "../../../core/services/invoice-review-summary.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Subject,
  switchMap,
} from "rxjs";
import { InvoiceProposalDto } from "../../../core/models/invoice-review/invoiceProposalDto";
import { OrderNotes } from "../../../core/models/invoice-review/orderNotes";
import { ProjectInvoiceReportSalesInvoiceList } from "../../../core/models/orders/getProjectInvoiceReportSalesResponseDto";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { ProposalListRequestDto } from "../../../core/models/invoice-review/proposalListRequestDto";
import { LoaderService } from "../../../core/services/loader.service";
import { OrderListingPaginatedService } from "../../../core/services/order-listing-paginated.service";
import { exportExcel } from "../../../core/services/shared.service";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { HidedynamicsdatePipe } from "../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { InvoiceReviewSummaryComponent } from "../invoice-review-summary/invoice-review-summary.component";
import { ModalStackerComponent } from "../../../shared/components/modal-stacker/modal-stacker.component";
import { InvoiceReviewOrderNotesComponent } from "../invoice-review-order-notes/invoice-review-order-notes.component";
import { ClientDetailsComponent } from "../../clients/components/client-details/client-details.component";
import { OrderSummaryComponent } from "../../orders/components/order-summary/order-summary.component";
import { InvoicePdfDialogueComponent } from "../../orders/components/invoice-pdf-dialogue/invoice-pdf-dialogue.component";
import { RadioButtonClickEvent, RadioButtonModule } from "primeng/radiobutton";
import { FilterMetadata } from "primeng/api";
import { GenericSortRequestDto } from "../../../core/models/generic-sort-request.dto";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";

@UntilDestroy()
@Component({
  selector: "jni-invoice-review-listing-paginated",
  templateUrl: "./invoice-review-listing-paginated.component.html",
  styleUrls: ["./invoice-review-listing-paginated.component.scss"],
  imports: [
    TableModule,
    CommonModule,
    HidedynamicsdatePipe,
    InvoiceReviewSummaryComponent,
    ModalStackerComponent,
    InvoiceReviewOrderNotesComponent,
    ClientDetailsComponent,
    OrderSummaryComponent,
    InvoicePdfDialogueComponent,
    RadioButtonModule,
  ],
})
export class InvoiceReviewListingPaginatedComponent
  implements OnInit, OnChanges
{
  @Input() inoviceReviewDisplayModal = false;

  invoiceReviewList: InvoiceProposalDto[] = [];
  invoiceReviewSummaryDisplayModal = false;
  invoiceReviewNumber = "";
  saleOrderId = "";
  saleOrderIdModal = "";
  invoiceReviewListingModalStatus = false;
  orderNotesGridListing: OrderNotes[] = [];
  defaultTake = 50;
  totalRecords = 0;
  lazyLoadEvent!: TableLazyLoadEvent;
  public orderBilling$ = new Subject<string>();
  filterTextChanged: Subject<Event | InvoiceProposalDto[]> = new Subject<
    Event | InvoiceProposalDto[]
  >();
  filterDebounceTime = 1000;
  clientDisplayModal = false;
  orderDisplayModal = false;
  customerId = "";
  selectedRadioButton = "Open";

  invoicePdfDialogue = false;
  invoiceList: ProjectInvoiceReportSalesInvoiceList[] = [];

  constructor(
    private _invoiceReviewSummaryService: InvoiceReviewSummaryService,
    private _loaderService: LoaderService,
    private renderer: Renderer2,
    private _orderListingPaginatedService: OrderListingPaginatedService,
    private _toastr: JniToastrService
  ) {}

  ngOnInit(): void {
    this.getOrderBilling();
  }

  ngOnChanges() {
    if (this.inoviceReviewDisplayModal) {
      this.invoiceReviewListingModalStatus = true;
    }
  }

  exportExcel() {
    exportExcel(this.invoiceReviewList, "Invoice Review");
  }

  onHoverBillingComments(invoiceProposalId: string, billingComment: string) {
    this.orderNotesGridListing = [];
    if (invoiceProposalId && billingComment) {
      this.orderBilling$.next(invoiceProposalId);
    }
  }

  getAllInvoiceReviewListingData(event: TableLazyLoadEvent) {
    this.lazyLoadEvent = event;
    const eventFilterArray = Object.keys(event.filters ?? {});
    const filter: GenericFilterRequestDto[] = eventFilterArray.map(x => {
      const tableFilter = event?.filters?.[x] as FilterMetadata;
      const data = {
        columnName: x ?? "",
        matchMode: tableFilter.matchMode ?? "",
        tableName: "",
        value: tableFilter.value ?? "",
      } as GenericFilterRequestDto;

      return data;
    });

    const sortArray: GenericSortRequestDto[] =
      event.multiSortMeta?.map(x => {
        return {
          ...x,
        } as GenericSortRequestDto;
      }) ?? [];

    filter.forEach(x => {
      if (x.columnName == "invoiceReviewNumber") {
        x.columnName = "ProposalIdD365";
        x.tableName = "ProjectInvoiceProposal";
      }
      if (x.columnName == "date") {
        x.columnName = "InvoiceDateD365";
        x.tableName = "ProjectInvoiceProposal";
      }
      if (x.columnName == "customerName") {
        x.columnName = "CustomerNameD365";
        x.tableName = "ProjectInvoiceProposal";
      }
      if (x.columnName == "customerNumber") {
        x.columnName = "CustomerAccountD365";
        x.tableName = "ProjectInvoiceProposal";
      }
      if (x.columnName == "orderNumber") {
        x.columnName = "SalesOrderIdD365";
        x.tableName = "ProjectInvoiceProposal";
      }
      if (x.columnName == "ReviewStatus") {
        if ((x.value as string).toLowerCase().startsWith("i")) {
          x.columnName = "LinePropertyD365";
          x.tableName = "ProjectInvoiceProposal";
        } else if (
          x.value &&
          "canceled".includes((x.value as string).toLowerCase())
        ) {
          x.columnName = "LinePropertyD365";
          x.tableName = "ProjectInvoiceProposal";
        } else {
          x.columnName = "ReviewStatusD365";
          x.tableName = "ProjectInvoiceProposal";
        }
      }
    });
    const request: ProposalListRequestDto = {
      skip: event.first ?? 0,
      take: event.rows ?? this.defaultTake,
      filters: filter,
      sort: sortArray,
      status: this.selectedRadioButton,
    };
    this._loaderService.setLoaderState(true);
    this._invoiceReviewSummaryService
      .getInvoiceReviewListingPaginated(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.data) {
            this.invoiceReviewList = res.data;
            this.totalRecords = res.totalRecords ?? 0;
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  getOrderBilling() {
    this.orderBilling$
      .pipe(
        debounceTime(200),
        switchMap((invoiceProposalId: string) => {
          if (invoiceProposalId) {
            return this._invoiceReviewSummaryService.getSalesOrderNotesWithTypeInvoiceProposal(
              invoiceProposalId
            );
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: res => {
          if (res.data) {
            if (res?.data?.length > 0) {
              const billingNotes = res.data.filter(x =>
                x?.type?.toLowerCase()?.includes("proposal")
              );
              this.orderNotesGridListing = billingNotes;
            }
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  openInvoice(invoiceReviewNumber: string, saleOrderId: string) {
    this.invoiceReviewNumber = invoiceReviewNumber;
    this.saleOrderId = saleOrderId;
    this.invoiceReviewSummaryDisplayModal = true;
  }

  closeModalForInvoiceReview() {
    this.invoiceReviewSummaryDisplayModal = false;
  }

  addScollHiddenClass() {
    if (this.invoiceReviewListingModalStatus) {
      this.renderer.addClass(document.body, "p-overflow-hidden");
    } else {
      this.renderer.removeClass(document.body, "p-overflow-hidden");
    }
  }

  orderNotesDisplayModal = false;
  orderNotesDisplayModalFunction(status: boolean) {
    this.orderNotesDisplayModal = status;
  }

  closeModalForOrderNotes() {
    this.orderNotesDisplayModal = false;
  }

  onFilterInput($event: Event | InvoiceProposalDto[]) {
    if (this.filterTextChanged.observers.length === 0) {
      this.filterTextChanged
        .pipe(debounceTime(this.filterDebounceTime), distinctUntilChanged())
        .subscribe(() => {
          this.getAllInvoiceReviewListingData(this.lazyLoadEvent);
        });
    }
    this.filterTextChanged.next($event);
  }

  closeClientModal() {
    this.clientDisplayModal = false;
  }

  closeModalForSaleOrder() {
    this.orderDisplayModal = false;
  }

  redirectToClientSummary(customerId: string) {
    if (customerId) {
      this.customerId = customerId;
      this.clientDisplayModal = true;
    }
  }

  redirectToSummary(saleOrderId: string) {
    if (saleOrderId) {
      this.saleOrderIdModal = saleOrderId;
      this.orderDisplayModal = true;
    }
  }

  getOrderOnChange(value: RadioButtonClickEvent) {
    if (value.value == "Approved") {
      this.selectedRadioButton = "Approve";
    } else {
      this.selectedRadioButton = value.value;
    }

    this.getAllInvoiceReviewListingData(this.lazyLoadEvent);
  }

  onStatusClick(salesOrderId: string) {
    if (salesOrderId) {
      this._loaderService.setLoaderState(true);
      this._orderListingPaginatedService
        .getProjectInvoiceReportSales({
          SalesOrderId: salesOrderId,
        })
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res?.status?.toLowerCase() == "success") {
              if (res.data) {
                if (res.data.InvoiceCount == 1) {
                  this._loaderService.setLoaderState(true);
                  this._orderListingPaginatedService
                    .getProjectInvoiceReport({
                      InvoiceDate: res.data.InvoiceList[0]?.InvoiceDate ?? "",
                      ProjectInvoiceId:
                        res.data.InvoiceList[0]?.ProjectInvoiceId,
                    })
                    .pipe(untilDestroyed(this))
                    .subscribe({
                      next: res => {
                        this._loaderService.setLoaderState(false);
                        if (res.data?.reportBase64String) {
                          const blob = this.b64toBlob(
                            res.data.reportBase64String,
                            "application/pdf"
                          );
                          const blobUrl = URL.createObjectURL(blob);
                          window.open(blobUrl);
                        } else {
                          this._toastr.error(
                            "Error",
                            res.message || "Failed To Fetch Invoice PDF"
                          );
                        }
                      },
                      error: () => {
                        this._loaderService.setLoaderState(false);
                        this._toastr.error(
                          "Error",
                          "Failed To Fetch Invoice PDF"
                        );
                      },
                    });
                } else if (res.data.InvoiceCount > 1) {
                  this._loaderService.setLoaderState(false);
                  this.invoiceList = res.data.InvoiceList;
                  this.invoicePdfDialogue = true;
                } else {
                  this._loaderService.setLoaderState(false);
                  this._toastr.error(
                    "Error",
                    res.message || "Failed To Fetch Invoice PDF"
                  );
                }
              } else {
                this._loaderService.setLoaderState(false);
                this._toastr.error(
                  "Error",
                  res.message || "Failed To Fetch Invoice PDF"
                );
              }
            } else {
              this._loaderService.setLoaderState(false);
              this._toastr.error(
                "Error",
                res.message || "Failed To Fetch Invoice PDF"
              );
            }
          },
          error: () => {
            this._loaderService.setLoaderState(false);
            this._toastr.error("Error", "Failed To Fetch Invoice PDF");
          },
        });
    }
  }

  b64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  closeUrlDialog() {
    this.invoicePdfDialogue = false;
  }

  scrollHiddenStatus() {
    setTimeout(() => {
      this.renderer.removeClass(document.body, "p-overflow-hidden");
    }, 0);
  }
}
